import { useCallback, useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import OperationsPageShell from "../components/OperationsPageShell";
import APICallService from "../../../../api/apiCallService";
import { operations } from "../../../../api/apiEndPoints";
import { operationsJSON } from "../../../../api/apiJSON/operations";
import { BranchHealthLevel, IBranchHealthRow } from "../../../../types";

// Suggested Operations module polling interval (OperationsDefaults.RefreshIntervalSeconds = 60).
const REFRESH_INTERVAL_MS = 60 * 1000;

// `level` is server-controlled and is the single source of truth for card color/label —
// don't recompute these from `score`.
const levelLabelMap: Record<BranchHealthLevel, string> = {
  GOOD: "Healthy",
  WARNING: "Needs watching",
  SERIOUS: "Needs attention",
  CRITICAL: "Critical",
};

const levelColorMap: Record<BranchHealthLevel, string> = {
  GOOD: "#3ba272",
  WARNING: "#f2a900",
  SERIOUS: "#ff6b35",
  CRITICAL: "#d23c3c",
};

const levelDotClassMap: Record<BranchHealthLevel, string> = {
  GOOD: "bg-success",
  WARNING: "bg-warning",
  SERIOUS: "",
  CRITICAL: "bg-danger",
};

const levelTextClassMap: Record<BranchHealthLevel, string> = {
  GOOD: "text-success",
  WARNING: "text-warning",
  SERIOUS: "",
  CRITICAL: "text-danger",
};

const cardBorderClassByLevelMap: Record<BranchHealthLevel, string> = {
  GOOD: "border-success",
  WARNING: "border-warning",
  SERIOUS: "",
  CRITICAL: "border-danger",
};

// Renders unknown warning strings gracefully rather than failing closed on a hardcoded switch.
// Kept as a fallback for legacy `warnings[]` when `issues[]` is empty.
const warningCopyMap: Record<string, string> = {
  overdue_orders: "Has overdue orders",
  pending_orders_no_active_routes:
    "High pending order volume with no active routes",
  load_exceeds_available_tonnage: "Load exceeds available tonnage",
  fleet_utilization_low: "Fleet utilization is low",
};

const describeWarning = (warning: string) => warningCopyMap[warning] || warning;

const BranchHealth = () => {
  const [branches, setBranches] = useState<IBranchHealthRow[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const fetchBranchHealth = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const apiService = new APICallService(
        operations.branchHealth,
        operationsJSON.branchHealth({})
      );
      const response = await apiService.callAPI();
      setBranches(response?.records || []);
      setAsOf(response?.asOf || null);
    } catch (err) {
      setError("Unable to load branch live status. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranchHealth();

    intervalRef.current = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchBranchHealth();
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchBranchHealth]);

  const openOrdersFor = (b: IBranchHealthRow) =>
    b.pendingOrders + b.overdueOrders;

  const sortedBranches = [...branches].sort((a, b) => a.score - b.score);

  const scoreChartOptions: ApexCharts.ApexOptions = {
    chart: {
      type: "bar",
      fontFamily: "inherit",
      height: 280,
      toolbar: { show: false },
      dropShadow: { enabled: false },
    },
    plotOptions: {
      bar: {
        columnWidth: "40%",
        borderRadius: 4,
        distributed: true,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val}`,
      style: { fontSize: "11px" },
    },
    legend: { show: false },
    stroke: {
      show: false,
    },
    xaxis: {
      categories: sortedBranches.map((b) => b.branchName),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: {
          colors: "#1a1a1a",
          fontSize: "12px",
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        style: {
          colors: "#7e8299",
          fontSize: "12px",
        },
      },
    },
    colors: sortedBranches.map((b) => levelColorMap[b.level]),
    grid: {
      borderColor: "#e4e6ef",
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val: number) => `Score: ${val}`,
      },
    },
  };

  const scoreChartSeries = [
    {
      name: "Health score",
      data: sortedBranches.map((b) => b.score),
    },
  ];

  const formattedAsOf = asOf ? new Date(asOf).toLocaleString() : null;

  return (
    <OperationsPageShell
      title="Branch Live Status"
      subtitle={
        formattedAsOf
          ? `Auto-refreshes every 1 min · last ${formattedAsOf}`
          : "Auto-refreshes every 1 min"
      }
      action={
        <button
          type="button"
          className="uw-btn uw-btn-light uw-btn-sm btn btn-sm btn-light"
          onClick={fetchBranchHealth}
          disabled={loading}
        >
          ↻ {loading ? "Refreshing…" : "Refresh now"}
        </button>
      }
    >
      {error && (
        <div className="alert alert-danger mb-5" role="alert">
          {error}
        </div>
      )}

      {!error && !loading && sortedBranches.length === 0 && (
        <div className="alert alert-secondary mb-5" role="alert">
          No branches to display.
        </div>
      )}

      {sortedBranches.length > 0 && (
        <div className="card card-flush mb-5">
          <div className="card-header pt-5">
            <h3 className="card-title align-items-start flex-column">
              <span className="fw-bold fs-6 text-gray-800">
                Health score — all branches
              </span>
            </h3>
            <div className="card-toolbar">
              <span className="fs-8 text-muted">
                Sorted lowest → highest score (worst first)
              </span>
            </div>
          </div>
          <div className="card-body pt-2">
            <Chart
              options={scoreChartOptions}
              series={scoreChartSeries}
              type="bar"
              height={280}
            />
            <div className="d-flex flex-wrap gap-6 mt-2">
              {(Object.keys(levelLabelMap) as BranchHealthLevel[]).map(
                (level) => (
                  <div key={level} className="d-flex align-items-center">
                    <span
                      className="rounded-circle me-2"
                      style={{
                        width: 10,
                        height: 10,
                        backgroundColor: levelColorMap[level],
                        display: "inline-block",
                      }}
                    />
                    <span className="fs-8 text-muted">
                      {levelLabelMap[level]}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      <div className="row g-5">
        {sortedBranches.map((b) => (
          <div className="col-xl-4 col-lg-6 col-md-6" key={b.branchId}>
            <div
              className={`card card-flush border border-2 ${
                cardBorderClassByLevelMap[b.level]
              } h-100`}
              style={{ borderColor: levelColorMap[b.level] }}
            >
              <div className="card-body p-6">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <div className="fw-bolder fs-4 text-gray-900">
                      {b.branchName}
                    </div>
                    <div className="d-flex align-items-center mt-1">
                      <span
                        className={`rounded-circle me-2 ${
                          levelDotClassMap[b.level]
                        }`}
                        style={{
                          width: 8,
                          height: 8,
                          display: "inline-block",
                          backgroundColor: levelColorMap[b.level],
                        }}
                      />
                      <span
                        className={`fs-7 fw-semibold ${
                          levelTextClassMap[b.level]
                        }`}
                        style={{ color: levelColorMap[b.level] }}
                      >
                        {levelLabelMap[b.level] || "Unknown"}
                      </span>
                    </div>
                  </div>
                  <div className="text-end">
                    <div
                      className={`fw-bolder fs-2 ${
                        levelTextClassMap[b.level]
                      }`}
                      style={{ color: levelColorMap[b.level] }}
                    >
                      {b.score}
                    </div>
                    <div className="fs-9 text-muted">Score</div>
                  </div>
                </div>

                <div className="d-flex flex-column gap-1 mb-3">
                  <div className="d-flex justify-content-between fs-7">
                    <span className="text-muted">Active routes</span>
                    <span className="fw-semibold text-gray-800">
                      {b.activeRoutes}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between fs-7">
                    <span className="text-muted">Pending orders</span>
                    <span className="fw-semibold text-gray-800">
                      {b.pendingOrders}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between fs-7">
                    <span className="text-muted">Overdue orders</span>
                    <span className="fw-semibold text-gray-800">
                      {b.overdueOrders}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between fs-7">
                    <span className="text-muted">Open orders</span>
                    <span className="fw-semibold text-gray-800">
                      {openOrdersFor(b)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between fs-7">
                    <span className="text-muted">Unprocessed orders</span>
                    <span className="fw-semibold text-gray-800">
                      {b.unprocessedOrders}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between fs-7">
                    <span className="text-muted">Orders out for delivery</span>
                    <span className="fw-semibold text-gray-800">
                      {b.ordersOut}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between fs-7">
                    <span className="text-muted">Weight to ship (t)</span>
                    <span className="fw-semibold text-gray-800">
                      {b.weightToShip}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between fs-7">
                    <span className="text-muted">Available tonnage (t)</span>
                    <span className="fw-semibold text-gray-800">
                      {b.availableTonnage}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between fs-7">
                    <span className="text-muted">Enroute vehicles</span>
                    <span className="fw-semibold text-gray-800">
                      {b.enrouteVehicles}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between fs-7">
                    <span className="text-muted">Active / total vehicles</span>
                    <span className="fw-semibold text-gray-800">
                      {b.activeVehicles} / {b.totalVehicles}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between fs-7">
                    <span className="text-muted">Pending clearance (fin/wh)</span>
                    <span className="fw-semibold text-gray-800">
                      {b.pendingClearanceFinance} / {b.pendingClearanceWarehouse}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between fs-7">
                    <span className="text-muted">Broken down vehicles</span>
                    <span className="fw-semibold text-gray-800">
                      {b.brokenDownVehicles}
                    </span>
                  </div>
                </div>

                {b.availableTonnage > 0 && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between fs-8 text-muted mb-1">
                      <span>Capacity used</span>
                      <span>
                        {Math.round(
                          Math.min(
                            (b.weightToShip / b.availableTonnage) * 100,
                            999
                          )
                        )}
                        %
                      </span>
                    </div>
                    <div
                      className="progress"
                      style={{ height: 6, backgroundColor: "#e4e6ef" }}
                    >
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{
                          width: `${Math.min(
                            (b.weightToShip / b.availableTonnage) * 100,
                            100
                          )}%`,
                          backgroundColor:
                            b.weightToShip > b.availableTonnage
                              ? "#d23c3c"
                              : "#3ba272",
                        }}
                      />
                    </div>
                  </div>
                )}

                {b.issues.length > 0 ? (
                  <ul className="list-unstyled mb-0">
                    {b.issues.map((issue, idx) => (
                      <li
                        key={`${b.branchId}-issue-${idx}`}
                        className="fs-8 text-danger d-flex align-items-start mb-1"
                      >
                        <span className="me-2">⚠</span>
                        <span>{issue}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  b.warnings.length > 0 && (
                    <ul className="list-unstyled mb-0">
                      {b.warnings.map((warning) => (
                        <li
                          key={warning}
                          className="fs-8 text-danger d-flex align-items-start mb-1"
                        >
                          <span className="me-2">⚠</span>
                          <span>{describeWarning(warning)}</span>
                        </li>
                      ))}
                    </ul>
                  )
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </OperationsPageShell>
  );
};

export default BranchHealth;
